import { type FormEvent, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogContentText from '@mui/material/DialogContentText'
import DialogTitle from '@mui/material/DialogTitle'
import TextField from '@mui/material/TextField'
import { ErrorCode } from 'common/errors'
import { GenericHttpError } from 'common/http/httpError'

export const isReauthenticationRequired = (error: unknown) =>
    error instanceof GenericHttpError &&
    typeof error.body !== 'string' &&
    error.body.type === ErrorCode.reauthenticationRequired

export const ReauthenticationForm = ({
    titleId,
    reauthenticate,
    onAuthenticated,
    onCancel,
    onBusyChange,
}: {
    titleId?: string
    reauthenticate: (password: string) => Promise<void>
    onAuthenticated: () => Promise<void>
    onCancel: () => void
    onBusyChange: (busy: boolean) => void
}) => {
    const [password, setPassword] = useState('')
    const [busy, setBusy] = useState(false)
    const [error, setError] = useState<string>()

    const submit = async (event: FormEvent) => {
        event.preventDefault()
        if (!password || busy) return
        setBusy(true)
        onBusyChange(true)
        setError(undefined)
        try {
            await reauthenticate(password)
            setPassword('')
            await onAuthenticated()
        } catch (failure) {
            let message = 'The password could not be confirmed. Check it and try again.'
            if (
                failure instanceof GenericHttpError &&
                typeof failure.body !== 'string' &&
                typeof failure.body.detail === 'string' &&
                failure.body.detail
            ) {
                message = failure.body.detail
            }
            setError(message)
        } finally {
            setBusy(false)
            onBusyChange(false)
        }
    }

    let feedback = <>Continue</>
    if (busy) feedback = <CircularProgress size={20} />

    return (
        <Box component="form" onSubmit={(event) => void submit(event)}>
            <DialogTitle id={titleId}>Confirm your password</DialogTitle>
            <DialogContent>
                <DialogContentText sx={{ mb: 2 }}>
                    Enter your current password to continue changing your sign-in methods.
                </DialogContentText>
                <TextField
                    autoFocus
                    fullWidth
                    label="Current password"
                    type="password"
                    autoComplete="current-password"
                    value={password}
                    onChange={(event) => setPassword(event.target.value)}
                />
                {error && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                        {error}
                    </Alert>
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onCancel} disabled={busy}>
                    Cancel
                </Button>
                <Button type="submit" variant="contained" disabled={!password || busy}>
                    {feedback}
                </Button>
            </DialogActions>
        </Box>
    )
}
